/* GrowthOS — Product form sections (used by addProduct.jsx for Create/Edit).
   Config-driven on the shared gos-cl-* / gos-dd-* form primitives (same pattern as
   salesForm/leadForm/taskForm). Replaces the legacy Formik + SelectBox + Bootstrap
   grid. All field handlers keep the exact container contract (handleChangeForm by
   name; handleChange({value,label,__isNew__}, idKey) → handleLookupDropDownChange;
   handleChangeCheckbox({target:{name,checked}})). Vendor/Manufacturer/Product
   Category stay CREATABLE (type a new value → posts the name without an id), matching
   the legacy CreatableSelect capability. */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getActiveOwners,
  getGenericMasterFilteredListByKey,
  getProductReferenceLookupByKey,
} from '../../redux/selector';
import { NameFormatter } from '../../shared/controls/formatters';

const ICONS = {
  note: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" />,
  tag: (<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z" /><circle cx="7" cy="7" r="1.4" /></>),
  grid: <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />,
  template: <path d="M4 4h16a1 1 0 0 1 1 1v4H3V5a1 1 0 0 1 1-1ZM3 9h7v11H4a1 1 0 0 1-1-1V9ZM10 9h11v6H10zM10 15h11v4a1 1 0 0 1-1 1H10v-5Z" />,
  chevD: <path d="M6 9l6 6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  search: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
  plus: <path d="M12 5v14M5 12h14" />,
};
const FIc = ({ name, size = 16, style, className }) => (
  <svg className={className} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {ICONS[name] || null}
  </svg>
);

const CURRENCY = process.env.REACT_APP_CURRENCY || '$';

// gos-dd Select — supports a `creatable` variant (type a value not in the list →
// "Create …" row → onChange({value, label, __isNew__:true})).
const Select = ({ value, placeholder, options, onChange, disabled, searchable, creatable }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const term = q.trim().toLowerCase();
  const shown = term ? options.filter((o) => String(o.label).toLowerCase().includes(term)) : options;
  const exact = options.some((o) => String(o.label).toLowerCase() === term);
  return (
    <div className="gos-dd" ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (value ? ' has-val' : '')} onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span className="gos-dd-label">{value || placeholder || 'Select…'}</span>
        <FIc name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open ? (
        <div className="gos-dd-pop">
          {searchable || creatable ? (
            <div className="gos-dd-search">
              <FIc name="search" size={14} />
              <input autoFocus placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          ) : null}
          <div className="gos-dd-list">
            {shown.length === 0 && !(creatable && term) ? <div className="gos-dd-empty">No options</div> : null}
            {shown.map((o) => (
              <button key={o.value} type="button" className={'gos-dd-item' + (o.label === value ? ' on' : '')} onClick={() => { onChange(o); setOpen(false); setQ(''); }}>
                <span className="gos-dd-grow">{o.label}</span>
                {o.label === value ? <FIc name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
            {creatable && term && !exact ? (
              <button type="button" className="gos-dd-item gos-dd-create" onClick={() => { onChange({ value: q.trim(), label: q.trim(), __isNew__: true }); setOpen(false); setQ(''); }}>
                <FIc name="plus" size={13} /> <span className="gos-dd-grow">Create “{q.trim()}”</span>
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ProductForm = ({ formData, handleChangeForm, handleChange, handleChangeCheckbox, errormsg }) => {
  const productOwner = useSelector(getActiveOwners);
  const handler = useSelector(getActiveOwners);
  const usageUnit = useSelector(getGenericMasterFilteredListByKey('UsageUnit'));
  const tax = useSelector(getGenericMasterFilteredListByKey('Tax'));
  const vendors = useSelector(getProductReferenceLookupByKey('Vendor'));
  const manufacturer = useSelector(getProductReferenceLookupByKey('Manufacturer'));
  const productCategory = useSelector(getProductReferenceLookupByKey('ProductCategory'));

  const opt = (list, vKey, lKey, fmt) => (list || []).map((o) => ({ value: o[vKey], label: fmt ? fmt(o[lKey]) : o[lKey] }));

  const renderField = (fl) => {
    const err = errormsg?.[fl.name];
    let control;
    if (fl.type === 'select') {
      control = (
        <Select
          value={formData?.[fl.display] || ''}
          placeholder={'Select ' + fl.label}
          options={fl.options}
          onChange={(o) => handleChange(o, fl.name)}
          searchable={fl.searchable}
          creatable={fl.creatable}
        />
      );
    } else if (fl.type === 'date') {
      control = (
        <input
          type="date"
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          value={formData?.[fl.name] ? String(formData[fl.name]).substring(0, 10) : ''}
          onChange={handleChangeForm}
        />
      );
    } else if (fl.type === 'money' || fl.type === 'percent') {
      control = (
        <div className="gos-qf-money">
          <span className="gos-qf-money-sym">{fl.type === 'money' ? CURRENCY : '%'}</span>
          <input
            className={'gos-cl-input' + (err ? ' err' : '')}
            type="number"
            min={0}
            step={fl.type === 'money' ? '0.01' : '1'}
            name={fl.name}
            placeholder={fl.type === 'money' ? '0.00' : '0'}
            value={formData?.[fl.name] ?? ''}
            onChange={handleChangeForm}
          />
        </div>
      );
    } else if (fl.type === 'checkbox') {
      const on = !!formData?.[fl.name];
      control = (
        <button
          type="button"
          className={'gos-qf-check' + (on ? ' on' : '')}
          onClick={() => handleChangeCheckbox({ target: { name: fl.name, checked: !on } })}
        >
          <span className={'lead-check sm' + (on ? ' on' : '')}>{on ? <FIc name="check" size={11} /> : null}</span>
          {fl.checkLabel || fl.label}
        </button>
      );
    } else if (fl.type === 'textarea') {
      control = (
        <textarea
          className={'gos-cl-input gos-cl-textarea' + (err ? ' err' : '')}
          name={fl.name}
          rows={4}
          placeholder={'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    } else {
      control = (
        <input
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          placeholder={'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    }
    return (
      <div className={'gos-cl-field' + (fl.full ? ' gos-cl-field-full' : '')} key={fl.name}>
        {fl.type === 'checkbox' ? null : (
          <label className="gos-cl-label">
            {fl.label}
            {fl.req ? <span className="gos-cl-req">*</span> : null}
          </label>
        )}
        {control}
        {err ? <span className="gos-cl-err">{err}</span> : null}
      </div>
    );
  };

  const sectionCard = (icon, title, sub, body) => (
    <div className="gos-lr-card gos-cl-sec">
      <div className="gos-cl-sec-head">
        <span className="gos-cl-sec-ic"><FIc name={icon} size={17} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-sec-title">{title}</div>
          <div className="gos-cl-sec-sub">{sub}</div>
        </div>
      </div>
      {body}
    </div>
  );

  const infoFields = [
    { label: 'Product Owner', name: 'productOwnerId', type: 'select', display: 'productOwner', searchable: true, options: opt(productOwner, 'userId', 'display_name', NameFormatter) },
    { label: 'Product Name', name: 'name', req: true },
    { label: 'Product Code', name: 'productCode' },
    { label: 'Vendor Name', name: 'vendorId', type: 'select', display: 'vendorName', creatable: true, options: opt(vendors, 'id', 'displayName') },
    { label: 'Manufacturer', name: 'manufacturerId', type: 'select', display: 'manufacturer', creatable: true, options: opt(manufacturer, 'id', 'displayName') },
    { label: 'Product Category', name: 'productCategoryId', type: 'select', display: 'productCategory', creatable: true, options: opt(productCategory, 'id', 'displayName') },
    { label: 'Sales Start Date', name: 'salesStartDate', type: 'date' },
    { label: 'Sales End Date', name: 'salesEndDate', type: 'date' },
    { label: 'Support Start Date', name: 'supportStartDate', type: 'date' },
    { label: 'Support End Date', name: 'supportEndDate', type: 'date' },
    { label: 'Product Active', name: 'productActive', type: 'checkbox', checkLabel: 'Mark product as active' },
  ];

  const priceFields = [
    { label: 'Unit Price', name: 'unitPrice', type: 'money' },
    { label: 'Commission Rate', name: 'commissionRate', type: 'percent' },
    { label: 'Tax', name: 'taxId', type: 'select', display: 'tax', options: opt(tax, 'id', 'displayName') },
  ];

  const stockFields = [
    { label: 'Usage Unit', name: 'usageUnitId', type: 'select', display: 'usageUnit', options: opt(usageUnit, 'id', 'displayName') },
    { label: 'Handler', name: 'handlerId', type: 'select', display: 'handler', searchable: true, options: opt(handler, 'userId', 'display_name', NameFormatter) },
    { label: 'Qty Ordered', name: 'quantityOrdered' },
    { label: 'Qty In Stock', name: 'quantityInStock' },
    { label: 'Qty In Demand', name: 'quantityInDemand' },
    { label: 'Reorder Level', name: 'reorderLevel' },
  ];

  const additionalFields = [
    { label: 'Description', name: 'description', type: 'textarea', full: true },
  ];

  return (
    <div className="gos-qf-body">
      {sectionCard('note', 'Product Information', 'Identity, ownership & availability',
        <div className="gos-cl-sec-body">{infoFields.map(renderField)}</div>)}
      {sectionCard('tag', 'Price Information', 'Unit price, commission & tax',
        <div className="gos-cl-sec-body">{priceFields.map(renderField)}</div>)}
      {sectionCard('grid', 'Stock Information', 'Inventory & handling',
        <div className="gos-cl-sec-body">{stockFields.map(renderField)}</div>)}
      {sectionCard('template', 'Additional Details', 'Notes & description',
        <div className="gos-cl-sec-body">{additionalFields.map(renderField)}</div>)}
    </div>
  );
};

export default ProductForm;
